import { getAPIRequest } from 'infrastructure/api'
import { INode } from 'shared/types/tree'
import {
  getRootRoute,
  getVersionMapping,
  goToTree,
  INodeMapping,
} from '../tree/TreeActions'
import { IMasterCurricula } from 'infrastructure/api'
import {
  createIdMap,
  filteredMappings,
  MappingFilterType,
} from '../version_mapping/utility'
import { NodeType } from 'shared/types/ingestion'
import { allNodesOfType, childNodesOfType } from '../utility/TreeHelpers'
import { getBadQuestions } from '../tree/masters/questions/QuestionEvaluation'
import { isInAssignableTree } from '../utility/TreeNavigation'
import { TreePresentationsManager } from '../tree/masters/presentations/TreePresentationsManager'
import { stagingData } from '../tree/utility/nodeIssues'
import { useCallback } from 'react'
import { useWithErrorNotification } from 'shared/ui/GlobalDialog'
import { makeKiddomProdGetRequest } from 'infrastructure/api'
import { getTree } from '../tree/TreeRequests'
import { doesTreeHaveXRefLinkIssues } from '../tree/nodeCrossAssociation/utility'

export function goToNode(node: {
  tree_id: string
  version: string
  id?: string
}) {
  goToTree({
    treeId: node.tree_id,
    versionId: node.version,
    nodeId: node.id,
  })
}

// The byValue param is what to key the result off of
// The sortedValue is what to sort the resulting list off of, in reverse order
export function getSortedByValue<T>(
  masters: any[],
  [byValue, sortedValue]: [string, string],
): { [key: string]: T[] } {
  const byTreeId = masters.reduce((acc: { [id: string]: any[] }, tree) => {
    const currentValue = tree[byValue]
    if (!acc[currentValue]) {
      acc[currentValue] = []
    }
    acc[currentValue].push(tree)
    return acc
  }, {})
  return Object.keys(byTreeId).reduce((acc: { [id: string]: any[] }, id) => {
    acc[id] = byTreeId[id].sort((b, a) =>
      a[sortedValue].localeCompare(b[sortedValue]),
    )
    return acc
  }, {})
}

export type MastersByTreeId = { [key: string]: IMasterCurricula[] }

export function useGetProdTree() {
  const makeRequest = useWithErrorNotification(
    { errorTitle: 'Error Retrieving Prod Tree' },
    makeKiddomProdGetRequest,
  )
  return useCallback(
    ({
      tree_id: treeId,
      version: versionId,
    }: {
      tree_id: string
      version: string
    }) => {
      return makeRequest<INode>({
        prodPath: getRootRoute({ treeId, versionId }),
      })
    },
    [makeRequest],
  )
}

export interface IMigrationInfo {
  master: {
    masterId: string
    title: string
  }
  fromVersion?: IMasterCurricula
  toVersion?: IMasterCurricula
  errors: string[]
  info: string[]
  missingMapping?: boolean
  incompleteMapping?: boolean
  migrationComplete?: boolean
  schoolCounts?: {
    fromVersion: number
    toVersion: number
  }
  mismatchedTypeMapping: INodeMapping[]
  oldVersionsWithSchools: IMasterCurricula[]
  dupMappings: { [k: string]: INodeMapping[] }
}

type TreeUseCountMap = {
  district: number[]
  school: number[]
}

export type TreeUseResponse = Record<string, TreeUseCountMap>

interface TreeUseCountWithTotal extends TreeUseCountMap {
  total: number
}

export async function getTreeCurriculaMap(
  treeId: string,
): Promise<Record<string, TreeUseCountWithTotal>> {
  return getAPIRequest<TreeUseResponse>(
    `/admin/primary/${treeId}/curricula_map`,
  ).then((newResult) => {
    return Object.entries(newResult).reduce(
      (acc, [key, { school, district }]) => {
        return {
          ...acc,
          [key]: {
            school,
            district,
            total: school.length + district.length,
          },
        }
      },
      {},
    )
  })
}

async function getMappingVerificationInfo({
  fromVersion,
  toVersion,
}: {
  fromVersion: IMasterCurricula
  toVersion: IMasterCurricula
}) {
  const mappingParams = {
    fromTree: {
      tree_id: fromVersion.treeId,
      version: fromVersion.version,
    },
    toTree: { tree_id: toVersion.treeId, version: toVersion.version },
  }
  const fromTreeParams = {
    treeId: fromVersion.treeId,
    versionId: fromVersion.version,
  }
  const toTreeParams = {
    treeId: toVersion.treeId,
    versionId: toVersion.version,
  }
  const { result: mapping } = await getVersionMapping(mappingParams)
  const { result: fromTree } = await getTree(fromTreeParams)
  const { result: toTree } = await getTree(toTreeParams)
  const sourceIdMapping = createIdMap(fromTree!)
  const destIdMapping = createIdMap(toTree!)
  return { mapping, fromTree, toTree, sourceIdMapping, destIdMapping }
}

// This pulls mapping info then sets status if it exists and is complete
export async function getMigrationVersionMappingInfo(info: IMigrationInfo) {
  const { fromVersion, toVersion } = info

  if (!fromVersion || !toVersion) {
    return info
  }

  const { mapping, sourceIdMapping, destIdMapping } =
    await getMappingVerificationInfo({ fromVersion, toVersion })
  if (!mapping?.matched.length && !mapping?.missing.length) {
    info.missingMapping = true
    info.errors.push('Mapping does not exist')
  } else if (mapping.missing.length) {
    info.incompleteMapping = true
    info.errors.push(`Mapping is incomplete: ${mapping.missing.length} missing`)
  }
  info.mismatchedTypeMapping = filteredMappings(
    mapping?.matched ?? [],
    sourceIdMapping,
    destIdMapping,
    MappingFilterType.CriticalMismatchedPaths,
  )
  info.dupMappings = mapping?.duplicates ?? {}
  if (info.mismatchedTypeMapping.length) {
    info.errors.push('Has mappings that must be fixed')
  }
  return info
}

export async function getMigrationSchoolCountInfo(
  info: IMigrationInfo,
  allMasters: { [key: string]: IMasterCurricula[] },
) {
  const { fromVersion, toVersion } = info
  const treeId = fromVersion?.treeId ?? toVersion?.treeId
  if (!treeId) return info
  const resp = await getTreeCurriculaMap(treeId)
  const fromKey = `${treeId}:${fromVersion?.version}`
  const toKey = `${treeId}:${toVersion?.version}`
  info.schoolCounts = {
    fromVersion: resp[fromKey]?.total,
    toVersion: resp[toKey]?.total,
  }
  if (!info.schoolCounts.fromVersion) {
    info.migrationComplete = true
    if (!info.schoolCounts.toVersion) {
      info.errors.push('No schools are associated with either version')
    }
  }
  Object.keys(resp).forEach((key) => {
    if (![fromKey, toKey].includes(key)) {
      const [id, version] = key.split(':')
      const prevMaster = allMasters[id]?.find((m) => m.version === version)
      prevMaster && info.oldVersionsWithSchools.push(prevMaster)
      info.info.push(`${resp[key].total} schools on ${prevMaster?.name}`)
    }
  })
  return info
}

export function getPresentationsWithSourceFile(tree: INode) {
  return allNodesOfType(tree, NodeType.Presentation).filter((n) => {
    const [attachmentsList, ...others] = childNodesOfType(
      n,
      NodeType.AttachmentsList,
    )
    if (!attachmentsList) {
      throw new Error('nolistwat?!')
    }
    if (others.length) {
      throw new Error('extralistswat??')
    }
    if (!attachmentsList.leaves?.length) {
      return false
    }
    if (attachmentsList.leaves.length > 1) {
      // throw new Error('leaves wat??') //TODO: why were we throwing here?
    }
    return !!attachmentsList?.leaves?.[0].body.source_file
  })
}

export interface IMasterUpdateValidations {
  treeId: string
  versionId: string
  badQuestions: string[]
  editablePresentations: INode[]
  presentationsFlagIssues: INode[]
  presentationsInAssignables: INode[]
  stagingImagesInDrawingQuestions: string[]
  hasXRefLinkIssues: boolean
}

export function getMasterUpdateValidations(
  newTree: INode,
): IMasterUpdateValidations {
  const manager = new TreePresentationsManager(newTree)
  return {
    treeId: newTree.tree_id,
    versionId: newTree.version,
    badQuestions: Object.keys(getBadQuestions(newTree)),
    editablePresentations: getPresentationsWithSourceFile(newTree),
    presentationsInAssignables: manager.getAssignablePresentations,
    presentationsFlagIssues: manager.presentationFlagsIssues,
    stagingImagesInDrawingQuestions:
      stagingData(newTree).questionNodesWithStagingImages,
    hasXRefLinkIssues: doesTreeHaveXRefLinkIssues(newTree),
  }
}

export function getPresentationsInAssignables(tree: INode) {
  const idMap = createIdMap(tree)
  const presentations = allNodesOfType(tree, NodeType.Presentation).filter(
    (presentation: INode) => {
      return isInAssignableTree(presentation, idMap)
    },
  )
  return presentations
}

export function hasMasterUpdateValidationErrors({
  badQuestions,
  editablePresentations,
  presentationsFlagIssues,
  stagingImagesInDrawingQuestions,
}: IMasterUpdateValidations) {
  return !![
    ...badQuestions,
    ...editablePresentations,
    ...presentationsFlagIssues,
    ...stagingImagesInDrawingQuestions,
  ].length
}
